import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Megaphone, Bot, PencilLine, Loader2, ArrowLeft, Save, ExternalLink, Copy, CheckCircle2, ImageIcon,
} from "lucide-react";
import { api, apiError, BACKEND_URL } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

const imgUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

export default function LandingBuilder() {
  const { gcId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [landing, setLanding] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data: gc } = await api.get(`/guided/campaigns/${gcId}`);
    setCampaign(gc);
    setTitle(gc.title && !["mi campaña", "campaña", "nueva campaña"].includes(gc.title.toLowerCase()) ? gc.title : "");
    if (gc.landing_id) {
      const { data: lp } = await api.get(`/landings/${gc.landing_id}`);
      setLanding(lp);
    }
  };

  useEffect(() => { load().catch(() => toast.error("No se pudo cargar la campaña")); }, [gcId]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (mode) => {
    if (!title.trim()) return toast.error("Primero ponle un nombre a tu campaña.");
    setBusy(true);
    try {
      const { data } = await api.post(`/guided/campaigns/${gcId}/landing`, { mode, title: title.trim() });
      setLanding(data);
      toast.success(mode === "ai" ? "¡Página creada por la IA! Revísala y ajústala." : "Página en blanco creada. Edítala a tu gusto.");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setField = (k, v) => setLanding((l) => ({ ...l, [k]: v }));
  const setBenefit = (i, v) => setLanding((l) => { const b = [...(l.benefits || ["", "", ""])]; b[i] = v; return { ...l, benefits: b }; });

  const toggleImage = (cid) => setLanding((l) => {
    const cur = l.image_content_ids || [];
    if (cur.includes(cid)) return { ...l, image_content_ids: cur.filter((x) => x !== cid) };
    if (cur.length >= 2) return { ...l, image_content_ids: [cur[1], cid] }; // mantén máximo 2
    return { ...l, image_content_ids: [...cur, cid] };
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/landings/${landing.landing_id}`, {
        title: title.trim(), headline: landing.headline, subheadline: landing.subheadline,
        intro: landing.intro, benefits: landing.benefits, story: landing.story,
        closing: landing.closing, privacy_note: landing.privacy_note,
        image_content_ids: landing.image_content_ids,
      });
      setLanding(data);
      toast.success("¡Cambios guardados!");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const publicUrl = landing ? `${window.location.origin}/p/${landing.slug}` : "";

  if (!campaign) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-purple-500" size={32} /></div>;

  return (
    <div data-testid="landing-builder" className="max-w-3xl">
      <button onClick={() => navigate("/dashboard/campanas")} className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-4">
        <ArrowLeft size={16} /> Volver a Campañas
      </button>
      <div className="flex items-center gap-3 mb-6">
        <Megaphone className="text-yellow-impact" size={26} />
        <h1 className="font-display font-black tracking-tight text-3xl">Página de Aterrizaje</h1>
      </div>

      {/* Paso 1: nombre de la campaña */}
      <div className="rounded-2xl border border-white/10 bg-[#121212] p-5 mb-5">
        <Label className="text-zinc-400 text-xs uppercase tracking-wider">1. Nombre de tu campaña <span className="text-yellow-impact">(obligatorio)</span></Label>
        <Input data-testid="campaign-name" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. Reto Detox 7 días — Marzo"
          className="mt-1.5 bg-black border-white/15 rounded-xl py-6 focus-visible:ring-yellow-400" />
        <p className="text-xs text-zinc-500 mt-1.5">Dale un nombre claro para identificar y organizar tus campañas.</p>
      </div>

      {!landing ? (
        /* Paso 2: crear */
        <div className="grid sm:grid-cols-2 gap-4" data-testid="landing-create-options">
          <div className="rounded-2xl border border-purple-500/30 bg-[#0e0a16] p-6 flex flex-col">
            <Bot className="text-purple-400" size={26} />
            <h3 className="font-display font-bold text-lg mt-3">2. Que la IA la cree</h3>
            <p className="text-sm text-zinc-400 flex-1 mt-1">La IA toma la información de esta campaña, la amplía y la hace más emocionante, y usa 2 de tus imágenes.</p>
            <Button data-testid="create-ai" onClick={() => create("ai")} disabled={busy} className="mt-4 bg-purple-600 hover:bg-purple-500 rounded-xl">
              {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <Bot size={16} className="mr-2" />} Crear con IA
            </Button>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black p-6 flex flex-col">
            <PencilLine className="text-yellow-impact" size={26} />
            <h3 className="font-display font-bold text-lg mt-3">2. Editarla yo mismo</h3>
            <p className="text-sm text-zinc-400 flex-1 mt-1">Empieza con una página en blanco y escribe tú los textos y elige las imágenes.</p>
            <Button data-testid="create-manual" onClick={() => create("manual")} disabled={busy} variant="outline" className="mt-4 border-yellow-400/40 hover:bg-yellow-400 hover:text-black rounded-xl">
              <PencilLine size={16} className="mr-2" /> Crear en blanco
            </Button>
          </div>
        </div>
      ) : (
        /* Paso 3: editor */
        <div className="space-y-5" data-testid="landing-editor">
          <div className="rounded-2xl border border-green-500/30 bg-green-500/5 p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-sm min-w-0">
              <CheckCircle2 className="text-green-400 shrink-0" size={18} />
              <span className="text-green-300 break-all">{publicUrl}</span>
            </div>
            <div className="flex gap-2">
              <Button data-testid="copy-url" size="sm" variant="outline" className="border-white/15 rounded-full" onClick={async () => { try { await navigator.clipboard.writeText(publicUrl); toast.success("Enlace copiado"); } catch { toast.error("Copia manual"); } }}>
                <Copy size={14} className="mr-1.5" /> Copiar
              </Button>
              <Button data-testid="open-url" size="sm" className="bg-yellow-400 hover:bg-yellow-300 text-black rounded-full" onClick={() => window.open(publicUrl, "_blank", "noopener")}>
                <ExternalLink size={14} className="mr-1.5" /> Ver
              </Button>
            </div>
          </div>

          <Field label="Titular"><Input data-testid="f-headline" value={landing.headline || ""} onChange={(e) => setField("headline", e.target.value)} className="bg-black border-white/15 rounded-xl" /></Field>
          <Field label="Subtitular"><Textarea data-testid="f-subheadline" value={landing.subheadline || ""} onChange={(e) => setField("subheadline", e.target.value)} className="bg-black border-white/15 rounded-xl" rows={2} /></Field>
          <Field label="Introducción"><Textarea data-testid="f-intro" value={landing.intro || ""} onChange={(e) => setField("intro", e.target.value)} className="bg-black border-white/15 rounded-xl" rows={3} /></Field>
          <Field label="Beneficios (3)">
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <Input key={i} data-testid={`f-benefit-${i}`} value={(landing.benefits || [])[i] || ""} onChange={(e) => setBenefit(i, e.target.value)} placeholder={`Beneficio ${i + 1}`} className="bg-black border-white/15 rounded-xl" />)}
            </div>
          </Field>
          <Field label="Historia / información ampliada (separa párrafos con |)"><Textarea data-testid="f-story" value={landing.story || ""} onChange={(e) => setField("story", e.target.value)} className="bg-black border-white/15 rounded-xl" rows={4} /></Field>
          <Field label="Frase de cierre"><Textarea data-testid="f-closing" value={landing.closing || ""} onChange={(e) => setField("closing", e.target.value)} className="bg-black border-white/15 rounded-xl" rows={2} /></Field>

          <Field label="Imágenes de la landing (elige 2)">
            {(campaign.images || []).length === 0 ? (
              <p className="text-xs text-yellow-impact flex items-center gap-1.5"><ImageIcon size={14} /> Esta campaña aún no tiene imágenes. Genera imágenes en la campaña para usarlas aquí.</p>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {(campaign.images || []).map((img) => {
                  const sel = (landing.image_content_ids || []).includes(img.content_id);
                  const order = (landing.image_content_ids || []).indexOf(img.content_id);
                  return (
                    <button key={img.content_id} type="button" data-testid="landing-img-option" onClick={() => toggleImage(img.content_id)}
                      className={`relative rounded-xl overflow-hidden border-2 transition-all ${sel ? "border-yellow-400" : "border-white/10 hover:border-white/30"}`}>
                      <img src={imgUrl(img.image_url)} alt="" className="w-full h-24 object-cover" />
                      {sel && <span className="absolute top-1 right-1 bg-yellow-400 text-black text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{order === 0 ? "1" : "2"}</span>}
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-zinc-500 mt-2">La 1ª es la imagen grande (arriba); la 2ª aparece en el cuerpo.</p>
          </Field>

          <div className="flex flex-wrap gap-3 pt-2">
            <Button data-testid="save-landing" onClick={save} disabled={saving} className="bg-green-500 hover:bg-green-400 text-black font-bold rounded-xl px-6 py-5">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />} Guardar cambios
            </Button>
            <Button data-testid="regen-ai" onClick={() => create("ai")} disabled={busy} variant="outline" className="border-purple-500/40 hover:bg-purple-600 hover:text-white rounded-xl px-6 py-5">
              {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <Bot size={16} className="mr-2" />} Regenerar con IA
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="rounded-2xl border border-white/10 bg-[#121212] p-5">
    <Label className="text-zinc-400 text-xs uppercase tracking-wider block mb-2">{label}</Label>
    {children}
  </div>
);
